/**
 * Legal pages — Privacy Policy, Terms of Service, Contact.
 * Kept in a single file because they share the same layout shell and appear
 * together in the site footer. Copy is drafted for a small marketplace at
 * pre-Series-A scale; a lawyer should review before scaling paid marketing.
 */
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Mail, MapPin, Instagram } from "lucide-react";

const UPDATED = "February 2026";
const CONTACT_EMAIL = "hello@theblkcommunity.com";
const PARENT = "Midas Touch Group LLC";

function LegalShell({ title, subtitle, children, testId }) {
  return (
    <div className="min-h-screen bg-tbc-cream/40" data-testid={testId}>
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <header className="mb-8">
          <p className="text-[11px] uppercase tracking-widest text-tbc-navy/50 font-semibold">
            Last updated · {UPDATED}
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl text-tbc-navy mt-2">{title}</h1>
          {subtitle && <p className="text-gray-600 mt-3 max-w-2xl">{subtitle}</p>}
        </header>
        <article className="prose prose-slate max-w-none prose-headings:font-heading prose-headings:text-tbc-navy prose-a:text-tbc-navy prose-strong:text-tbc-navy space-y-6 text-[15px] leading-relaxed text-gray-800">
          {children}
        </article>
      </main>
      <Footer />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Privacy Policy
// ---------------------------------------------------------------------------
export function PrivacyPage() {
  return (
    <LegalShell
      testId="privacy-page"
      title="Privacy Policy"
      subtitle={`How ${PARENT} (operator of The Blk Community) collects, uses, and protects your information.`}
    >
      <section>
        <h2>1. Who we are</h2>
        <p>
          The Blk Community (the &quot;Service&quot;) is operated by {PARENT}. When we say
          &quot;we&quot;, &quot;us&quot;, or &quot;our&quot;, we mean {PARENT}. Contact us anytime at{" "}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        </p>
      </section>

      <section>
        <h2>2. Information we collect</h2>
        <p>We collect only what we need to run the Service:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Account info</strong>: name, email, and (if you sign in with Google) the profile photo Google returns. If you sign up with email + password, we store only a one-way <em>bcrypt hash</em> of the password — never the password itself.</li>
          <li><strong>Business info</strong>: anything you or your business enters (name, description, category, address, hours, product listings, logo/photos).</li>
          <li><strong>Transaction info</strong>: order line items, shipping addresses, and payout status. Card numbers never touch our servers — see §5.</li>
          <li><strong>Analytics</strong>: aggregate page views, referral clicks, coarse device type. We use both Google Analytics 4 and a lightweight first-party counter. IPs are truncated before storage.</li>
          <li><strong>Support chat</strong>: transcripts of any conversation you have with our AI assistant so we can escalate to a human ticket if needed.</li>
        </ul>
      </section>

      <section>
        <h2>3. How we use it</h2>
        <ul className="list-disc pl-6 space-y-1">
          <li>To operate the directory, marketplace, and payouts.</li>
          <li>To send transactional email (order confirmations, claim approvals, weekly digests you opted into) via Resend.</li>
          <li>To calculate referral rewards (TBC Points).</li>
          <li>To detect fraud and abuse.</li>
        </ul>
        <p>
          We <strong>do not sell your personal information.</strong> We do not share your email with third-party marketing lists.
        </p>
      </section>

      <section>
        <h2>4. Who we share it with</h2>
        <p>Only the vendors required to run the platform:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Stripe & PayPal</strong> — payment processing. We store an internal <em>order ID</em>; card data lives with them, not us.</li>
          <li><strong>Resend</strong> — outgoing transactional email.</li>
          <li><strong>Google (OAuth, Places, Analytics)</strong> — social login, business verification, and coarse traffic analytics.</li>
          <li><strong>Emergent</strong> — hosting and AI (chat / OCR / image generation) via the Emergent LLM Key.</li>
        </ul>
        <p>We disclose information when compelled by valid legal process. We&apos;ll always try to notify you first if we&apos;re legally allowed to.</p>
      </section>

      <section>
        <h2>5. Payments — we never see your card</h2>
        <p>
          All card payments run through <strong>Stripe</strong> or <strong>PayPal</strong>. When you check out, your card details go directly to those processors over TLS and we receive only a token + the amount paid. No card number, CVC, or expiry is ever stored on our servers or in our database. Refunds are issued through the same processor.
        </p>
      </section>

      <section>
        <h2>6. Security</h2>
        <ul className="list-disc pl-6 space-y-1">
          <li>Passwords are hashed with <strong>bcrypt</strong> (cost factor 12) — never stored, transmitted, or logged in plaintext.</li>
          <li>Sessions use signed HTTP-only cookies; a fresh token is issued on every login.</li>
          <li>All API endpoints require authentication + role checks; admins can only be assigned by an existing admin.</li>
          <li>MongoDB is our source of truth. Row-level access is enforced by our API — clients never talk to the database directly, so there are no client-side security rules to misconfigure (unlike Supabase or Firebase).</li>
          <li>Automated daily backups; encryption at rest and in transit.</li>
        </ul>
      </section>

      <section>
        <h2>7. Your rights</h2>
        <p>Regardless of where you live you can email <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> to:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Access a copy of your data.</li>
          <li>Correct or update anything inaccurate.</li>
          <li>Delete your account and every business profile you own.</li>
          <li>Opt out of any non-transactional email.</li>
        </ul>
        <p>We aim to respond within 30 days.</p>
      </section>

      <section>
        <h2>8. Cookies</h2>
        <p>
          We use a small number of first-party cookies for login sessions and analytics. Third-party cookies come from Google Analytics 4. You can block cookies in your browser without breaking core functionality (you just won&apos;t stay signed in).
        </p>
      </section>

      <section>
        <h2>9. Children</h2>
        <p>The Service is not directed at children under 13. If you believe a child has provided personal information to us, contact <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> and we&apos;ll delete it.</p>
      </section>

      <section>
        <h2>10. Changes</h2>
        <p>We&apos;ll post any material changes here and update the &quot;Last updated&quot; date. For meaningful changes we&apos;ll also email active accounts.</p>
      </section>

      <section>
        <h2>11. Contact</h2>
        <p>
          {PARENT}<br />
          Email: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
        </p>
      </section>
    </LegalShell>
  );
}

// ---------------------------------------------------------------------------
// Terms of Service
// ---------------------------------------------------------------------------
export function TermsPage() {
  return (
    <LegalShell
      testId="terms-page"
      title="Terms of Service"
      subtitle="Please read these terms carefully — using the Service means you agree to them."
    >
      <section>
        <h2>1. Agreement</h2>
        <p>
          These Terms govern your use of The Blk Community website, apps, and services (the &quot;Service&quot;) operated by {PARENT}. By creating an account, listing a business, or purchasing a product, you agree to be bound by these Terms and our <a href="/privacy">Privacy Policy</a>.
        </p>
      </section>

      <section>
        <h2>2. Eligibility</h2>
        <p>You must be at least 18 years old and legally able to enter into a contract in your jurisdiction. Business listings must be for real, operating businesses that are majority Black-owned or explicitly serve the Black community.</p>
      </section>

      <section>
        <h2>3. Accounts</h2>
        <p>
          You&apos;re responsible for keeping your credentials confidential and for any activity under your account. Tell us immediately at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> if you suspect unauthorized access. We reserve the right to suspend accounts that violate these Terms.
        </p>
      </section>

      <section>
        <h2>4. Business listings</h2>
        <ul className="list-disc pl-6 space-y-1">
          <li>You warrant that you have the right to list every business you claim or create.</li>
          <li>You&apos;re responsible for the accuracy of your listing information, products, and pricing.</li>
          <li>We may verify, feature, hide, or remove listings at our discretion — including for spam, fraud, hate content, or repeated policy violations.</li>
          <li>Founding Business status is limited to the first 100 approved businesses and is non-transferable.</li>
        </ul>
      </section>

      <section>
        <h2>5. Payments & fees</h2>
        <ul className="list-disc pl-6 space-y-1">
          <li>All payments are processed by Stripe or PayPal. Their terms also apply.</li>
          <li>Subscription tiers (Growth, Accelerator) auto-renew monthly unless cancelled. Cancel anytime in your Dashboard — access continues through the end of the paid period.</li>
          <li>Platform fees (4% on Growth, 2.5% on Accelerator) are deducted at checkout before payouts.</li>
          <li>Refunds are at the seller&apos;s discretion; disputes involving fraud are moderated by us in line with Stripe/PayPal dispute rules.</li>
        </ul>
      </section>

      <section>
        <h2>6. TBC Points</h2>
        <p>Points earned via referrals or purchases have no cash value, cannot be sold, and expire if your account has no activity for 24 months. Points can only be redeemed on the Service.</p>
      </section>

      <section>
        <h2>7. Prohibited use</h2>
        <p>You may not use the Service to post illegal, hateful, deceptive, or infringing content; to spam other users; to scrape data at scale; to reverse-engineer the platform; or to impersonate anyone. We enforce this aggressively.</p>
      </section>

      <section>
        <h2>8. Intellectual property</h2>
        <p>
          All Service code, branding, and non-user content is © {PARENT}. By posting content (photos, products, descriptions) you grant us a worldwide, royalty-free license to display, distribute, and promote that content in connection with the Service.
        </p>
      </section>

      <section>
        <h2>9. Warranty disclaimer</h2>
        <p>The Service is provided &quot;as is&quot; and &quot;as available&quot;. We don&apos;t guarantee that every listing is accurate, that transactions will complete, or that the Service will be uninterrupted.</p>
      </section>

      <section>
        <h2>10. Limitation of liability</h2>
        <p>To the maximum extent permitted by law, {PARENT} is not liable for indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, arising from your use of the Service. Our total liability is capped at the greater of (a) $100 or (b) the amount you paid us in the 12 months before the claim.</p>
      </section>

      <section>
        <h2>11. Termination</h2>
        <p>You may close your account any time. We may suspend or terminate accounts that violate these Terms; on termination, your right to use the Service ends immediately.</p>
      </section>

      <section>
        <h2>12. Governing law</h2>
        <p>These Terms are governed by the laws of the State of Maryland, USA, without regard to conflict-of-laws principles. Disputes will be resolved in the state or federal courts located in Prince George&apos;s County, Maryland.</p>
      </section>

      <section>
        <h2>13. Changes</h2>
        <p>We&apos;ll post material updates here and update the &quot;Last updated&quot; date at the top of this page.</p>
      </section>

      <section>
        <h2>14. Contact</h2>
        <p>Questions? Email <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.</p>
      </section>

      {/* ================================================================= */}
      {/* Appendix A — Data Processing Addendum for sellers who handle       */}
      {/* customer personal data. Explains their obligations under CCPA/GDPR */}
      {/* since we operate as a Processor and they act as Controllers of    */}
      {/* their own customer relationships.                                  */}
      {/* ================================================================= */}
      <section className="not-prose mt-12 rounded-2xl border-2 border-tbc-gold/40 bg-tbc-cream/40 p-6" data-testid="terms-dpa-appendix">
        <p className="text-[11px] uppercase tracking-widest text-tbc-gold font-semibold">Appendix A</p>
        <h2 className="font-heading text-2xl text-tbc-navy mt-1 mb-4">
          Data Processing Addendum — for businesses processing customer data
        </h2>
        <p className="text-sm text-gray-700 leading-relaxed">
          If you sell on The Blk Community you collect customer data (name,
          shipping address, contact info, order history) directly and through us.
          In legal terms, <strong>you are the &quot;Data Controller&quot;</strong> of your
          customers&apos; personal data and <strong>we ({PARENT}) act as your
          &quot;Data Processor&quot;</strong> only for the portion routed through the platform.
          That distinction is important under CCPA, CPRA, and GDPR.
        </p>

        <div className="mt-5 space-y-4 text-sm text-gray-800">
          <div>
            <h3 className="font-heading text-tbc-navy">Your obligations as a seller</h3>
            <ul className="list-disc pl-6 mt-1 space-y-1 leading-relaxed">
              <li>Post a plain-language <strong>privacy notice</strong> on any external site or landing page you drive traffic to, explaining what you collect and why.</li>
              <li>Respond to individual data-subject requests (access, deletion, correction, opt-out of sale) within 30 days if you&apos;re CPRA/GDPR-scoped. We&apos;ll forward any requests we receive about your customers to you within 5 business days.</li>
              <li>Never share customer contact info with third parties without a lawful basis.</li>
              <li>Encrypt or securely store any customer data you export off the platform.</li>
              <li>If you experience a data breach affecting customers introduced through TBC, notify us within <strong>72 hours</strong> at <a className="text-tbc-navy underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> so we can coordinate downstream notifications.</li>
            </ul>
          </div>

          <div>
            <h3 className="font-heading text-tbc-navy">Our commitments as processor</h3>
            <ul className="list-disc pl-6 mt-1 space-y-1 leading-relaxed">
              <li>We process customer data only to run the platform (order routing, payouts, transactional email). No secondary use, no ad-targeting, no sale.</li>
              <li>Sub-processors (Stripe, PayPal, Resend, Google, Emergent) are contractually bound to equivalent data-protection standards.</li>
              <li>We delete a seller&apos;s customer records within 30 days of account closure, except records we&apos;re legally required to retain (tax, dispute history).</li>
              <li>Encryption in transit (TLS 1.2+) and at rest for all customer PII.</li>
            </ul>
          </div>

          <div>
            <h3 className="font-heading text-tbc-navy">CCPA / CPRA specifics (California residents)</h3>
            <p className="mt-1 leading-relaxed">
              We do not &quot;sell&quot; personal information as defined by CCPA/CPRA. California customers can email us or the seller directly to exercise Right to Know, Right to Delete, Right to Correct, and Right to Opt-Out of Sale/Share.
            </p>
          </div>

          <div>
            <h3 className="font-heading text-tbc-navy">GDPR specifics (EEA / UK residents)</h3>
            <p className="mt-1 leading-relaxed">
              Data transfers outside the EEA rely on Standard Contractual Clauses. EEA/UK data subjects may lodge complaints with their local supervisory authority; we&apos;ll cooperate with any lawful investigation.
            </p>
          </div>

          <div>
            <h3 className="font-heading text-tbc-navy">Signing the DPA</h3>
            <p className="mt-1 leading-relaxed">
              By listing a business and processing customer orders on TBC you accept this Appendix as-is. Enterprise sellers requiring a counter-signed DPA (with specific SCC modules or breach-notification SLAs) can request one at <a className="text-tbc-navy underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
            </p>
          </div>
        </div>
      </section>
    </LegalShell>
  );
}

// ---------------------------------------------------------------------------
// Contact
// ---------------------------------------------------------------------------
export function ContactPage() {
  return (
    <LegalShell
      testId="contact-page"
      title="Contact us"
      subtitle="We read every message. Fastest response is by email."
    >
      <div className="grid sm:grid-cols-2 gap-4 not-prose">
        <a
          href={`mailto:${CONTACT_EMAIL}`}
          className="rounded-2xl border border-tbc-navy/10 bg-white p-5 hover:shadow-md transition"
          data-testid="contact-email"
        >
          <Mail className="w-6 h-6 text-tbc-gold" />
          <p className="font-heading text-lg text-tbc-navy mt-3">Email</p>
          <p className="text-sm text-gray-600 mt-1 break-all">{CONTACT_EMAIL}</p>
        </a>
        <a
          href="https://instagram.com/theblkcommunity"
          target="_blank"
          rel="noreferrer"
          className="rounded-2xl border border-tbc-navy/10 bg-white p-5 hover:shadow-md transition"
          data-testid="contact-instagram"
        >
          <Instagram className="w-6 h-6 text-tbc-gold" />
          <p className="font-heading text-lg text-tbc-navy mt-3">Instagram</p>
          <p className="text-sm text-gray-600 mt-1">@theblkcommunity</p>
        </a>
      </div>

      <section>
        <h2>Business claims & verification</h2>
        <p>Have a listing you want to claim or something you&apos;d like updated? Email us the business name and any proof (website domain match, ownership document, or a photo of you at the storefront). We usually turn these around within 24-48 hours.</p>
      </section>

      <section>
        <h2>Press & partnerships</h2>
        <p>For media inquiries, sponsorships, or collaboration ideas, reach out at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> with &quot;Press&quot; or &quot;Partnership&quot; in the subject line.</p>
      </section>

      <section>
        <h2>Mailing address</h2>
        <p className="flex items-start gap-2">
          <MapPin className="w-4 h-4 mt-1 text-tbc-gold" />
          {PARENT} — mail intake handled via email. Physical mailing address available on request.
        </p>
      </section>
    </LegalShell>
  );
}
